import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Progressive enhancement for the cart upsell cards.
 *
 * The floor behaviour is a plain HTML form POST to /cart/add - that works with this
 * file absent, blocked, or broken. Everything here only upgrades that into an
 * in-place add so the drawer never navigates.
 *
 * Two deliberate safety properties:
 *
 * 1. A single module-level lock disables *every* upsell button (across the drawer
 *    and the cart page) for the whole duration of an add, including the drawer
 *    re-render. Overlapping clicks are structurally impossible rather than merely
 *    unlikely.
 * 2. If the fetch hangs - this store runs several third-party embeds, at least one
 *    of which monkey-patches the global fetch - we stop waiting after
 *    FALLBACK_TIMEOUT_MS and hand the add back to the browser via a native form
 *    submission, so the customer still ends up with the item.
 */

const FALLBACK_TIMEOUT_MS = 2000;
const BUTTON_SELECTOR = '[data-cart-upsell-add]';
const SEEN_ATTRIBUTE = '_cart_upsell_seen';
const ATTRIBUTE_PREFIX = '_cart_upsell_';

/** Guards against overlapping adds. Module-level so it spans every instance. */
let inFlight = false;

/**
 * Tiers whose impression we have already recorded during this page load. The cart
 * itself is the durable record (via `data-seen`); this only stops a morph from
 * re-posting the same tier before the server-rendered value catches up.
 */
const recordedThisLoad = new Set();

/**
 * Enables or disables every upsell button on the page.
 * Re-queried each time because `morphSection` swaps this markup out from under us.
 * @param {boolean} disabled
 */
function setAllButtonsDisabled(disabled) {
  for (const button of document.querySelectorAll(BUTTON_SELECTOR)) {
    if (button instanceof HTMLButtonElement) {
      button.disabled = disabled;
      button.setAttribute('aria-busy', disabled ? 'true' : 'false');
    }
  }
}

/**
 * Section ids that should be re-rendered by the add request, matching how the
 * theme's own product form refreshes the cart.
 * @returns {string}
 */
function cartSectionIds() {
  const ids = new Set();

  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      ids.add(component.dataset.sectionId);
    }
  }

  return Array.from(ids).join(',');
}

class CartUpsellComponent extends HTMLElement {
  /**
   * `morph` reuses this element across cart updates and only rewrites its
   * attributes, so `connectedCallback` does NOT run again when the cart crosses a
   * tier boundary. Watching `data-tier` is what catches the customer progressing
   * from one tier into the next - without it the denominator silently under-counts
   * every tier a customer arrives at rather than starts in.
   */
  static get observedAttributes() {
    return ['data-tier'];
  }

  /**
   * @param {string} name
   * @param {string | null} previous
   * @param {string | null} next
   */
  attributeChangedCallback(name, previous, next) {
    if (name !== 'data-tier' || previous === next || !next) return;
    if (!this.isConnected) return;

    this.recordImpression();
  }

  connectedCallback() {
    this.addEventListener('submit', this.#handleSubmit);

    // A morph can reconnect this element mid-add; keep the lock visible if so.
    if (inFlight) setAllButtonsDisabled(true);

    this.recordImpression();
  }

  /**
   * Records that this tier was shown, giving the analytics a denominator: without
   * it you know a tier produced N conversions but not whether it was shown 200
   * times or 4000, so tiers cannot be compared.
   *
   * Writes at most once per tier per cart - `data-seen` is rendered server-side
   * from the cart, so no read is needed first, and a cart carries at most three
   * of these writes over its whole life.
   *
   * Deliberately does NOT dispatch a cart event: this is a bookkeeping write, and
   * announcing it would trigger a section re-render, which would re-run this code.
   */
  async recordImpression() {
    const tier = this.dataset.tier;
    if (!tier || inFlight || recordedThisLoad.has(tier)) return;

    const seen = (this.dataset.seen || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    if (seen.includes(tier)) return;

    recordedThisLoad.add(tier);
    seen.push(tier);

    try {
      await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({ attributes: { [SEEN_ATTRIBUTE]: seen.join(',') } }),
        })
      );
      this.dataset.seen = seen.join(',');
    } catch (error) {
      // Never let analytics bookkeeping affect the customer's ability to buy.
      recordedThisLoad.delete(tier);
      console.warn('[cart-upsell] could not record impression:', error);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#handleSubmit);
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Another add is still settling - swallow this one rather than racing it.
    if (inFlight) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.#addToCart(form);
  };

  /** @param {HTMLFormElement} form */
  async #addToCart(form) {
    inFlight = true;
    setAllButtonsDisabled(true);

    const formData = new FormData(form);
    const sections = cartSectionIds();
    if (sections) {
      formData.append('sections', sections);
      // Render those sections against the current URL, so the markup that comes
      // back carries a correct `return_to` for its own no-JS fallback.
      formData.append('sections_url', window.location.pathname);
    }

    // If anything below hangs or throws, let the browser do the add natively.
    let settled = false;
    const fallback = setTimeout(() => {
      if (settled) return;
      settled = true;
      this.#submitNatively(form);
    }, FALLBACK_TIMEOUT_MS);

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, config);
      const result = await response.json();

      if (settled) return; // the timeout already handed off to a native submit

      settled = true;
      clearTimeout(fallback);

      if (result.status) {
        // Shopify reported a real problem with the add (e.g. it sold out between
        // render and click). Surface it rather than silently doing nothing.
        console.error('[cart-upsell] add to cart failed:', result.message ?? result);
        this.#release();
        return;
      }

      // Refresh the cart UI through the theme's own event, so the drawer, the cart
      // page and the header count all update the way they normally do.
      this.dispatchEvent(
        new CartAddEvent({}, String(formData.get('id') ?? ''), {
          source: 'cart-upsell-component',
          itemCount: 1,
          sections: result.sections,
        })
      );

      // `morphSection` runs synchronously inside that dispatch, so the drawer has
      // finished re-rendering by the time we get here - only now is it safe to
      // accept another click.
      this.#release();
    } catch (error) {
      if (settled) return;
      settled = true;
      clearTimeout(fallback);

      console.error('[cart-upsell] falling back to native submit:', error);
      this.#submitNatively(form);
    }
  }

  /**
   * Hands the add back to the browser. The form carries `return_to`, so this
   * reloads the current page rather than dumping the customer on /cart.
   * @param {HTMLFormElement} form
   */
  #submitNatively(form) {
    // A section re-render can bake in a `return_to` for whatever path the section
    // was rendered against (often "/"), which would bounce the customer to the
    // home page. Point it at where they actually are before handing over.
    const returnTo = form.querySelector('input[name="return_to"]');
    if (returnTo instanceof HTMLInputElement) {
      returnTo.value = window.location.pathname + window.location.search;
    }

    // Leave the lock on: the page is navigating, and re-enabling would only invite
    // a second click against a document that is about to be replaced.
    form.submit();
  }

  #release() {
    inFlight = false;
    setAllButtonsDisabled(false);

    // An add usually moves the cart into a new tier, and that morph runs while the
    // lock above is still held - so the impression for the tier the customer just
    // arrived at is skipped. Sweep once the add has fully settled.
    for (const component of document.querySelectorAll('cart-upsell-component')) {
      if (component instanceof CartUpsellComponent) component.recordImpression();
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}

/**
 * Clears upsell markers from an empty cart.
 *
 * Cart attributes are NOT cleared by /cart/clear.js, nor by removing the last line
 * item - they outlive the cart that earned them. Without this, someone who adds an
 * upsell sticker, removes it, then buys something else still arrives at checkout
 * carrying `_cart_upsell_tag`, which tags the order as an upsell conversion that
 * never happened and inflates every attach-rate number built on it.
 *
 * Only ever runs against an empty cart, so it cannot discard a live conversion.
 */
async function clearResidueIfCartEmpty() {
  try {
    const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
    if (cart.item_count > 0) return;

    const stale = Object.keys(cart.attributes || {}).filter((key) => key.startsWith(ATTRIBUTE_PREFIX));
    if (stale.length === 0) return;

    // Shopify removes a cart attribute when it is set to an empty string.
    const cleared = Object.fromEntries(stale.map((key) => [key, '']));

    await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', { body: JSON.stringify({ attributes: cleared }) })
    );

    recordedThisLoad.clear();
    for (const component of document.querySelectorAll('cart-upsell-component')) {
      if (component instanceof HTMLElement) component.dataset.seen = '';
    }
  } catch (error) {
    console.warn('[cart-upsell] could not clear stale attributes:', error);
  }
}

// If a cart update arrives from anywhere else while we are idle, make sure we never
// leave the buttons stuck in a disabled state.
document.addEventListener(ThemeEvents.cartUpdate, () => {
  if (!inFlight) setAllButtonsDisabled(false);

  // Covers the common path: the customer removes the last item they had.
  clearResidueIfCartEmpty();
});

// Covers the returning visitor whose cart cookie still carries markers from a cart
// they emptied in an earlier session. The server-rendered flag keeps this free
// unless there is genuinely something to clear.
if (window.CartUpsell?.itemCount === 0 && window.CartUpsell?.hasResidue) {
  clearResidueIfCartEmpty();
}
